import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { playClickSound } from "@/lib/sounds";
import { useAdminPendingCount } from "@/hooks/useAdminPendingCount";
import { useUserRoles } from "@/hooks/useUserRoles";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Building2,
  FileText,
  BarChart3,
  Trophy,
  Settings,
  LogOut,
  FileQuestion,
  ArrowLeftRight,
  Timer,
  Plus,
  Minus,
  PlayCircle,
  Brain,
  AlertTriangle,
  Flame,
  Award,
  Target,
} from "lucide-react";
import { cn } from "@/lib/utils";
import brandLogoHorizontal from "@/assets/brand/nivela-logo-horizontal.png";
import brandLogoHorizontalDarkAsset from "@/assets/brand/nivela-logo-horizontal-dark.png.asset.json";
const brandLogoHorizontalDark = brandLogoHorizontalDarkAsset.url;
import brandIcon from "@/assets/brand/nivela-icon.png";
import type { ModuleConfig } from "@/lib/modules/registry";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Props {
  config: ModuleConfig;
}

export function ModuleSidebar({ config }: Props) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { isAdmin, pendingCount } = useAdminPendingCount();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [studyOpen, setStudyOpen] = useState(true);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const prefix = config.routePrefix;
  const { gradientActive } = config.theme;

  const { isAdmin: isAdminRole, isDeveloper } = useUserRoles();
  const canSeeVideoaulas = isAdminRole || isDeveloper;

  const studyItems: MenuItem[] = [
    { title: config.sidebar.questionsLabel ?? `Questões ${config.displayName}`, url: `${prefix}/questions`, icon: FileQuestion },
    { title: "Simulados", url: `${prefix}/simulations`, icon: FileText },
    { title: "Plano de Estudos", url: `${prefix}/study-plan`, icon: Target },
    ...(config.sidebar.showSmartReview ? [{ title: "Meu Caderno", url: `${prefix}/smart-review`, icon: Brain }] : []),
    ...(config.sidebar.showWeakAreas ? [{ title: "Áreas Fracas", url: `${prefix}/weak-areas`, icon: Flame }] : []),
    ...(canSeeVideoaulas ? [{ title: "Videoaulas", url: "/videoaulas", icon: PlayCircle }] : []),
    ...(config.sidebar.showPomodoro ? [{ title: "Pomodoro", url: `${prefix}/pomodoro`, icon: Timer }] : []),
  ];

  const analysisItems: MenuItem[] = [
    { title: "Ranking", url: `${prefix}/ranking`, icon: Trophy },
    { title: "Desempenho", url: `${prefix}/analytics`, icon: BarChart3 },
    ...(config.sidebar.showErrorPatterns ? [{ title: "Padrões de Erro", url: `${prefix}/error-patterns`, icon: AlertTriangle }] : []),
    ...(config.sidebar.showAchievements ? [{ title: "Conquistas", url: `${prefix}/achievements`, icon: Award }] : []),
  ];

  const settingsItems: MenuItem[] = [
    { title: "Configurações", url: `${prefix}/settings`, icon: Settings },
  ];

  const { profile, userInitials } = useProfile();


  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const isActive = (url: string) => location.pathname === url;

  const renderMenuItems = (items: MenuItem[]) => (
    <SidebarMenu className="space-y-1 px-2">
      {items.map((item, index) => {
        const active = isActive(item.url);
        return (
          <SidebarMenuItem
            key={item.url}
            className="animate-fade-in"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <SidebarMenuButton asChild tooltip={item.title}>
              <a
                href={item.url}
                onClick={(e) => {
                  e.preventDefault();
                  playClickSound();
                  navigate(item.url);
                }}
                className={cn(
                  "sidebar-menu-item flex items-center gap-3 px-3 py-2.5 rounded-full transition-all duration-200",
                  active ? `${gradientActive} text-white font-medium` : "hover:bg-muted/60",
                )}
              >
                <item.icon className={cn("sidebar-icon h-4 w-4 flex-shrink-0", active && "scale-110")} />
                {!collapsed && <span className="truncate">{item.title}</span>}
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  const CollapsibleHeader = ({
    label,
    isOpen,
    onToggle,
  }: {
    label: string;
    isOpen: boolean;
    onToggle: () => void;
  }) => (
    <button
      onClick={onToggle}
      className="sidebar-group-header flex items-center w-full px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground font-medium hover:text-foreground transition-colors"
    >
      <span>{label}</span>
      {isOpen ? (
        <Minus className="sidebar-expand-icon h-3.5 w-3.5 ml-auto" />
      ) : (
        <Plus className="sidebar-expand-icon h-3.5 w-3.5 ml-auto" />
      )}
    </button>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className="border-b border-border/50 p-4">
        <div className="flex items-center gap-3">
          {collapsed ? (
            <img src={brandIcon} alt="Nível A Educação" className="h-9 w-9 rounded-xl flex-shrink-0" />
          ) : (
            <div className="flex flex-col gap-0.5">
              <img src={brandLogoHorizontal} alt="Nível A Educação" className="h-7 w-auto block dark:hidden" />
              <img src={brandLogoHorizontalDark} alt="Nível A Educação" className="h-7 w-auto hidden dark:block" />
              {config.headerSubtitle && (
                <span className="text-xs text-muted-foreground">{config.headerSubtitle}</span>
              )}
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="flex flex-col px-2 py-4">
        <SidebarGroup className="px-2">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={`Dashboard ${config.displayName}`}>
                  <a
                    href={prefix}
                    onClick={(e) => { e.preventDefault(); navigate(prefix); }}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-full transition-all duration-200",
                      isActive(prefix) ? `${gradientActive} text-white font-medium` : "hover:bg-muted/60",
                    )}
                  >
                    <Building2 className={cn("h-4 w-4 flex-shrink-0", isActive(prefix) && "scale-110")} />
                    {!collapsed && <span>Dashboard {config.displayName}</span>}
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Collapsible open={studyOpen} onOpenChange={setStudyOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader label="Estudar" isOpen={studyOpen} onToggle={() => setStudyOpen(!studyOpen)} />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>{renderMenuItems(studyItems)}</SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && <SidebarGroupContent>{renderMenuItems(studyItems)}</SidebarGroupContent>}
          </SidebarGroup>
        </Collapsible>

        <Collapsible open={analysisOpen} onOpenChange={setAnalysisOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader label="Analisar" isOpen={analysisOpen} onToggle={() => setAnalysisOpen(!analysisOpen)} />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>{renderMenuItems(analysisItems)}</SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && <SidebarGroupContent>{renderMenuItems(analysisItems)}</SidebarGroupContent>}
          </SidebarGroup>
        </Collapsible>

        <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen} className="group/collapsible">
          <SidebarGroup>
            {!collapsed && (
              <CollapsibleTrigger asChild>
                <CollapsibleHeader label="Configurações" isOpen={settingsOpen} onToggle={() => setSettingsOpen(!settingsOpen)} />
              </CollapsibleTrigger>
            )}
            <CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              <SidebarGroupContent>{renderMenuItems(settingsItems)}</SidebarGroupContent>
            </CollapsibleContent>
            {collapsed && <SidebarGroupContent>{renderMenuItems(settingsItems)}</SidebarGroupContent>}
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>

      <SidebarFooter className="border-t border-border/50 p-4">
        {config.moduleType !== 'policiais' && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Trocar Módulo">
                <a
                  href="/select-module"
                  onClick={(e) => { e.preventDefault(); navigate("/select-module"); }}
                  className={cn(
                    "flex items-center rounded-full border border-border bg-muted/50 hover:bg-muted transition-all duration-200 shadow-sm hover:shadow-md mb-2",
                    collapsed ? "justify-center w-10 h-10 p-0 mx-auto" : "gap-3 px-3 py-2.5",
                  )}
                >
                  <ArrowLeftRight className={cn("flex-shrink-0", collapsed ? "h-5 w-5" : "h-4 w-4")} />
                  {!collapsed && <span className="font-medium">Trocar Módulo</span>}
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        {isAdmin && (
          <NavLink to="/admin" className="block px-2 mb-2" activeClassName="">
            <div className="relative flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2 hover:bg-muted transition-colors">
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              {!collapsed && <span className="text-xs text-muted-foreground">Painel Admin</span>}
              {pendingCount > 0 &&
                (collapsed ? (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-destructive" />
                ) : (
                  <span className="ml-auto bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5 text-[10px] font-medium min-w-[18px] text-center">
                    {pendingCount}
                  </span>
                ))}
            </div>
          </NavLink>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <div className={cn("flex items-center w-full", collapsed ? "flex-col gap-2" : "gap-2")}>
              <SidebarMenuButton
                tooltip={profile?.first_name || "Perfil"}
                className="flex-1 min-w-0 overflow-visible"
                onClick={() => navigate("/profile")}
              >
                <div className="flex items-center gap-3 w-full group">
                  <Avatar className="h-9 w-9 flex-shrink-0 ring-2 ring-border transition-all duration-200 group-hover:ring-foreground/20">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback
                      className={cn(
                        gradientActive.replace("bg-gradient-to-r", "bg-gradient-to-br"),
                        "text-white text-sm font-medium",
                      )}
                    >
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  {!collapsed && (
                    <div className="flex flex-col items-start text-left min-w-0">
                      <span className="text-sm font-medium truncate w-full">{profile?.first_name || "Usuário"}</span>
                      <span className="text-xs text-muted-foreground truncate w-full">{user?.email}</span>
                    </div>
                  )}
                </div>
              </SidebarMenuButton>
              <button
                onClick={handleSignOut}
                className="p-2 rounded-xl hover:bg-muted/60 transition-all duration-200 text-destructive flex-shrink-0"
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
